import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import Search from "@mui/icons-material/Search";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGridWithDefaultSort } from "components/DataGridWithDefaultSort";
import { Dialog } from "components/Dialog";
import CopyableTypography from "components/Typography/CopyableTypography";
import { JobLog } from "generated-graphql/graphql";
import { useMemo, useState } from "react";
import { prettyPrintDateTimeWithSeconds } from "util/dates";

const logColumns: GridColDef<JobLog>[] = [
  {
    field: "status",
    headerName: "Status",
    width: 200,
  },
  {
    field: "createdAt",
    headerName: "Logged At",
    width: 150,
    renderCell: ({ row }) => (
      <span>{prettyPrintDateTimeWithSeconds(row.createdAt)}</span>
    ),
  },
  {
    field: "errorMessage",
    headerName: "Message",
    flex: 0.5,
    renderCell: ({ row }) => <MessageCell message={row.errorMessage} />,
  },
];

function MessageCell({ message }: { message?: string | null }) {
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const openDialog = () => setMessageDialogOpen(true);
  const closeDialog = () => setMessageDialogOpen(false);
  return (
    <>
      <Typography
        onClick={openDialog}
        variant="body2"
        sx={{ cursor: "pointer" }}
      >
        {message}
      </Typography>
      <Dialog
        open={messageDialogOpen}
        onClose={closeDialog}
        title="Log Message"
        noPrimaryAction
      >
        <CopyableTypography variant="body2" text={message} />
      </Dialog>
    </>
  );
}

export interface LogsTabProps {
  logs: JobLog[];
}

export function LogsTab({ logs }: LogsTabProps) {
  const [search, setSearch] = useState("");

  const filteredLogs = useMemo(() => {
    if (!search.trim()) {
      return logs;
    }
    const lower = search.toLowerCase();
    return logs.filter((log) =>
      (log.errorMessage ?? "").toLowerCase().includes(lower)
    );
  }, [logs, search]);

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search log message..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
      <DataGridWithDefaultSort
        sx={{ maxHeight: "800px" }}
        hideFooter
        sortModel={[{ field: "createdAt", sort: "desc" }]}
        columns={logColumns}
        rows={filteredLogs}
      />
    </Box>
  );
}
